/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Video, Hash, Clock, ChevronLeft, ChevronRight, ArrowLeft, Save, X, CheckCircle, Info, Search } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import PhonePreview from '../components/PhonePreview';
import Modal from '../components/Modal';
import isEmail from 'validator/lib/isEmail';
import toastService from '../utils/toastService';
import PermissionGuard from '../components/PermissionGuard';
import { getIndustryLabel } from '../constants/workflowOptions';
import { FIELD_LIMITS } from '../utils/constants';
import { validateTextField } from '../utils/validation';
import { resolveCustomerReferenceLabel } from '../utils/customerReferenceLabel';
import filesIcon from '../assets/files.svg';
import scannerIcon from '../assets/scanner.svg';

const typeIcons = { photo: Camera, video: Video, identifier: Hash };

const ERR_EXPIRY_MINUTES = 'Expiry must be at least 15 minutes';
const ERR_EXPIRY_HOURS = 'Expiry must be at least 1 hour';
const ERR_EXPIRY_DAYS = 'Expiry must be at least 1 day';

// Named constants 
const MS_PER_MINUTE = 60000;
const MS_PER_HOUR = 3600000;
const DEFAULT_EXPIRY_MINUTES = 1440; // 24 hours
const PHONE_E164_LENGTH = 11;
const PHONE_AREA_CODE_END = 3;
const PHONE_PREFIX_END = 6;
const PHONE_DIGITS_REQUIRED = 10;
const MIN_EXPIRY_MINUTES = process.env.REACT_APP_MIN_EXPIRY_MINUTES ? parseInt(process.env.REACT_APP_MIN_EXPIRY_MINUTES, 10) : 15;

// Shared UI literals (Sonar: S1192)
const CLASS_FIELD_INPUT = 'w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all';
const CLASS_FIELD_INPUT_DISABLED = 'disabled:opacity-50 disabled:bg-base-200';
const CLASS_FIELD_ERROR_BORDER = 'border-error focus:ring-error focus:border-error';
const CLASS_FIELD_DEFAULT_BORDER = 'border-base-300';
const CLASS_INFO_TOOLTIP = 'tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:w-max before:max-w-[260px] before:content-[attr(data-tip)] before:whitespace-normal before:text-left before:leading-snug after:border-r-[#fffdf2]';

const TIME_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' }
];

const formatExpiryLabel = (value, unit) => {
  if (!value || !unit) { return '—'; }
  switch (unit) {
    case 'minutes': return `${value} minute${value !== 1 ? 's' : ''}`;
    case 'hours': return `${value} hour${value !== 1 ? 's' : ''}`;
    case 'days': return `${value} day${value !== 1 ? 's' : ''}`;
    default: return `${value} ${unit}`;
  }
};

const convertToHours = (value, unit) => {
  switch (unit) {
    case 'minutes': return value / 60;
    case 'hours': return value;
    case 'days': return value * 24;
    default: return value;
  }
};

const parseExpiryFromMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes <= 0) { return { value: 24, unit: 'hours' }; }
  if (totalMinutes % (24 * 60) === 0) { return { value: totalMinutes / (24 * 60), unit: 'days' }; }
  if (totalMinutes % 60 === 0) { return { value: totalMinutes / 60, unit: 'hours' }; }
  return { value: totalMinutes, unit: 'minutes' };
};

const EditAssignment = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [workflowPreview, setWorkflowPreview] = useState(null);
  const [previewStep, setPreviewStep] = useState(0);

  const [submitterName, setSubmitterName] = useState('');
  const [submitterPhone, setSubmitterPhone] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [systemReferenceNumber, setSystemReferenceNumber] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [customUnit, setCustomUnit] = useState('hours');
  const [initialExpiry, setInitialExpiry] = useState(null);
  const [isWorkflowDropdownOpen, setIsWorkflowDropdownOpen] = useState(false);
  const [workflowSearch, setWorkflowSearch] = useState('');
  const workflowRef = useRef(null);
  const workflowSearchRef = useRef(null);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(
    workflows.find((workflow) => workflow.id === selectedWorkflow) || assignment
  );
  const [assigneeName, setAssigneeName] = useState('');
  const [customerReferenceNumber, setCustomerReferenceNumber] = useState('');
  const [restrictToSingleSession, setRestrictToSingleSession] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [expiring, setExpiring] = useState(false);
  const [showExpireModal, setShowExpireModal] = useState(false);

  const handleExpireToggle = () => setShowExpireModal(true);

  const rawWelcomeTitle = workflowPreview?.branding?.welcomeTitle || 'Welcome, {name}'
  const welcomeTitle = rawWelcomeTitle?.replaceAll('{name}', submitterName);

  const handleExpireConfirm = async () => {
    setShowExpireModal(false);
    setExpiring(true);
    try {
      await apiClient.patch(`/assignments/${id}/expire`);
      await fetchAssignment();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to expire assignment');
    } finally {
      setExpiring(false);
    }
  };

  const handleExpireCancel = () => setShowExpireModal(false);

  // Fetch assignment data
  const fetchAssignment = useCallback(async () => {
    try {
      const res = await apiClient.get(`/assignments/${id}`);
      const a = res.data;

      setAssignment(a);

      setSubmitterName(a.submitterName || '');
      setSubmitterPhone(a.submitterPhone || '');
      setSubmitterEmail(a.submitterEmail || '');
      setSystemReferenceNumber(a.systemReferenceNumber || '');
      setAssigneeName(a.assigneeName || '');
      setCustomerReferenceNumber(a.customerReferenceNumber || '');
      setSelectedWorkflow(a.workflowId || '');
      setRestrictToSingleSession(a.restrictToSingleSession ?? false); // Need to refactor

      // Parse expiry
      if (a.defaultExpiry && a.expiryTimeUnit) {
        setCustomValue(String(a.defaultExpiry));
        setCustomUnit(a.expiryTimeUnit);
        setInitialExpiry({ value: String(a.defaultExpiry), unit: a.expiryTimeUnit });
      } else {
        const remainingMins = Math.floor((new Date(a.expiresAt) - new Date()) / MS_PER_MINUTE);
        const parsed = parseExpiryFromMinutes(remainingMins > 0 ? remainingMins : DEFAULT_EXPIRY_MINUTES);
        setCustomValue(String(parsed.value));
        setCustomUnit(parsed.unit);
        setInitialExpiry({ value: String(parsed.value), unit: parsed.unit });
      }
    } catch (err) {
      setError('Failed to load assignment');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    Promise.all([
      apiClient.get('/workflows', { params: { limit: 1000 } }),
      fetchAssignment()
    ]).then(([wfRes]) => {
      const data = wfRes.data;
      if (Array.isArray(data)) {
        setWorkflows(data);
      } else if (data && Array.isArray(data.workflows)) {
        setWorkflows(data.workflows);
      } else {
        setWorkflows([]);
      }
    }).catch(() => {
      setWorkflows([]);
    });
  }, [fetchAssignment]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (workflowRef.current && !workflowRef.current.contains(event.target)) {
        setIsWorkflowDropdownOpen(false);
        setWorkflowSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync workflow preview when selectedWorkflow or workflows list changes
  useEffect(() => {
    if (!selectedWorkflow || workflows.length === 0) {
      setWorkflowPreview(null);
      setPreviewStep(0);
      return;
    }
    const wf = workflows.find(w => w.id === selectedWorkflow);
    setWorkflowPreview(wf || null);
    setPreviewStep(0);
  }, [selectedWorkflow, workflows]);

  const formatPhoneNumber = (value) => {
    let phoneNumber = value.replace(/[^\d]/g, '');
    if (!phoneNumber) { return ''; }
    // Strip leading country code '1' when pasting E.164 (+17327979714 → 7327979714)
    if (phoneNumber.length === PHONE_E164_LENGTH && phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.slice(1);
    }
    if (phoneNumber.length < 4) { return phoneNumber; }
    if (phoneNumber.length < 7) { return `(${phoneNumber.slice(0, PHONE_AREA_CODE_END)}) ${phoneNumber.slice(PHONE_AREA_CODE_END)}`; }
    return `(${phoneNumber.slice(0, PHONE_AREA_CODE_END)}) ${phoneNumber.slice(PHONE_AREA_CODE_END, PHONE_PREFIX_END)}-${phoneNumber.slice(PHONE_PREFIX_END, PHONE_DIGITS_REQUIRED)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setSubmitterPhone(formatted);
    if (formatted) {
      const digits = formatted.replace(/[^\d]/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        setFieldErrors(prev => ({ ...prev, submitterPhone: 'Phone number must be 10 digits' }));
      } else {
        setFieldErrors(prev => ({ ...prev, submitterPhone: '' }));
      }
    } else {
      setFieldErrors(prev => ({ ...prev, submitterPhone: '' }));
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setSubmitterEmail(value);
    if (value) {
      if (value.length > FIELD_LIMITS.EMAIL) {
        setFieldErrors(prev => ({ ...prev, submitterEmail: `Email cannot exceed ${FIELD_LIMITS.EMAIL} characters` }));
      } else if (!isEmail(value)) {
        setFieldErrors(prev => ({ ...prev, submitterEmail: 'Invalid email format' }));
      } else {
        setFieldErrors(prev => ({ ...prev, submitterEmail: '' }));
      }
    } else {
      setFieldErrors(prev => ({ ...prev, submitterEmail: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const currentErrors = {};

    // Validate Workflow Selection
    if (!selectedWorkflow) {
      currentErrors.selectedWorkflow = 'Please select a capture workflow';
      hasErrors = true;
    }

    const submitterError = validateTextField(submitterName, 'Recipient name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
    if (submitterError) {
      currentErrors.submitterName = submitterError;
      hasErrors = true;
    } else {
      currentErrors.submitterName = "";
      hasErrors = false;
    }

    // Validate Assignee Name if it's required
    if (assignment.status !== 'expired') {
      const assigneeError = validateTextField(assigneeName, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
      if (assigneeError) {
        currentErrors.assigneeName = assigneeError;
        hasErrors = true;
      } else {
        currentErrors.assigneeName = "";
        hasErrors = false;
      }
    }

    if (submitterPhone) {
      const digits = submitterPhone.replace(/[^\d]/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        currentErrors.submitterPhone = 'Phone number must be 10 digits';
        hasErrors = true;
      }
    }

    if (submitterEmail) {
      if (submitterEmail.length > FIELD_LIMITS.EMAIL) {
        currentErrors.submitterEmail = `Email cannot exceed ${FIELD_LIMITS.EMAIL} characters`;
        hasErrors = true;
      } else if (!isEmail(submitterEmail)) {
        currentErrors.submitterEmail = 'Invalid email format';
        hasErrors = true;
      } else {
        // Valid email format
      }
    }

    const crnError = validateTextField(customerReferenceNumber, dynamicCustomerReferenceLabel, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.REFERENCE });
    if (crnError) {
      currentErrors.customerReferenceNumber = crnError;
      hasErrors = true;
    } else {
      currentErrors.customerReferenceNumber = '';
    }

    setFieldErrors(currentErrors);

    if (hasErrors) {
      return;
    }

    setError('');

    // Validate expiry
    const expiryVal = parseInt(customValue, 10);
    if (isNaN(expiryVal) || expiryVal <= 0) {
      setError('Please enter a valid positive number for expiry');
      return;
    }
    if (customUnit === 'minutes' && expiryVal < MIN_EXPIRY_MINUTES) {
      setError(ERR_EXPIRY_MINUTES);
      return;
    }
    if (customUnit === 'hours' && expiryVal < 1) {
      setError(ERR_EXPIRY_HOURS);
      return;
    }
    if (customUnit === 'days' && expiryVal < 1) {
      setError(ERR_EXPIRY_DAYS);
      return;
    }

    setSaving(true);
    try {
      await apiClient.patch(`/assignments/${id}`, {
        workflowId: selectedWorkflow || undefined,
        submitterName,
        submitterPhone: submitterPhone?.trim() || null,
        submitterEmail: submitterEmail?.trim() || null,
        systemReferenceNumber: systemReferenceNumber || undefined,
        assigneeName: assigneeName || undefined,
        customerReferenceNumber: customerReferenceNumber || undefined,
        defaultExpiry: parseInt(customValue, 10),
        expiryTimeUnit: customUnit,
        restrictToSingleSession,
      });
      navigate(`/assignments/${id}`);
      toastService.success('Assignment updated successfully');
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('. ') : (msg || err.response?.data?.error || 'Failed to update assignment'));
    } finally {
      setSaving(false);
    }
  };

  const handleFieldBlur = useCallback((fieldKey, value, fieldLabel, options) => {
    const errorMsg = validateTextField(value, fieldLabel, options);
    setFieldErrors(prev => ({ ...prev, [fieldKey]: errorMsg }));
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs animate-pulse">Loading assignment...</p>
      </div>
    );
  }

  if (error && !assignment) {
    return (
      <div className="p-8">
        <div className="bg-error/10 border border-error/20 text-error px-5 py-4 rounded-2xl text-xs font-black uppercase tracking-widest">{error}</div>
      </div>
    );
  }

  // Block edit if opened, in_progress, or completed
  const blockEdit = !assignment || !['created', 'expired'].includes(assignment.status);
  const isReadOnly = assignment.status !== "created";

  // If the expiry is not changed, use the initial expiry
  const isInitialExpiryMatch = initialExpiry && customValue === initialExpiry.value && customUnit === initialExpiry.unit && assignment?.expiresAt;

  // Calculate the expiry date
  const expiresAtDate = isInitialExpiryMatch
    ? new Date(assignment.expiresAt)
    : new Date(
      Date.now() +
      convertToHours(parseInt(customValue, 10), customUnit) * MS_PER_HOUR
    );

  if (blockEdit) {
    return (
      <div className="p-8 text-center min-h-[400px] flex flex-col items-center justify-center">
        <div className="max-w-md bg-error/5 border border-error/10 text-error p-8 rounded-3xl">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock size={32} className="text-error" />
          </div>
          <h2 className="text-xl font-black mb-2 uppercase tracking-tight">Access Restricted</h2>
          <p className="text-sm font-medium opacity-70 mb-8 leading-relaxed">
            {assignment?.status === 'expired' && assignment.openedAt
              ? 'This assignment has expired and was previously opened by the recipient. For security and data integrity, it cannot be resent or edited.'
              : `Editing is not allowed once the recipient has ${assignment?.status === 'completed' ? 'completed' : 'opened'} the link.`}
          </p>
          <button
            onClick={() => navigate(`/assignments/${id}`)}
            className="flex items-center gap-2 mx-auto px-6 py-3 bg-error text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-error/90 transition-all shadow-lg shadow-error/20"
          >
            <ArrowLeft size={14} />
            Back to Assignment
          </button>
        </div>
      </div>
    );
  }

  const items = workflowPreview?.items || [];
  const branding = workflowPreview?.branding || {};
  const totalScreens = 2 + items.length + 1;
  const screenLabels = ['Welcome', 'Steps', ...items.map((_, i) => `#${i + 1}`), 'Done'];

  return (
    <div className="flex flex-col lg:flex-row h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      {/* Left: Main Content */}
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md border-b lg:border-b-0 lg:border-r border-base-300">
        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0">
          <button
            onClick={() => navigate(`/assignments/${id}`)}
            className="flex items-center gap-2 text-base-content/50 hover:text-primary mb-4 transition-all font-black uppercase tracking-widest text-[10px]"
          >
            <ArrowLeft size={14} />
            Back to Assignment
          </button>
          <div className="flex flex-wrap flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="page-title mb-1 flex items-baseline gap-2 min-w-0 max-w-full overflow-hidden">
                <span className="shrink-0">Edit Assignment</span>
                {workflowPreview && (
                  <span className="text-primary/60 font-normal text-2xl custom-line-break flex-1 min-w-0"> • {workflowPreview.name}</span>
                )}
              </h1>
              <p className="page-subtitle">Update the details for this media capture session</p>
            </div>            {/* Expire Link Toggle */}
            {assignment && assignment.secureToken && assignment.status !== 'completed' && assignment.status !== 'expired' && (
              <PermissionGuard permissions={['assignments.expire']}>
                <div className="flex items-center gap-3 ms-auto">
                  <span className="text-[10px] font-black text-base-content/60 uppercase tracking-widest">
                    {expiring ? 'Expiring...' : 'Expire Link'}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={assignment.status === 'expired'}
                      onClick={(e) => {
                        e.preventDefault();
                        if (assignment.status !== 'expired') {
                          handleExpireToggle();
                        }
                      }}
                      disabled={expiring || assignment.status === 'expired'}
                      readOnly
                    />
                    <div className="relative w-11 h-6 bg-base-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-error/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-error peer-disabled:opacity-50 peer-disabled:cursor-not-allowed">
                      {expiring && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </PermissionGuard>
            )}
          </div>
        </div>

        <div className="p-6 sm:p-8 flex-1">
          {isReadOnly && (
            <div className="mb-6 flex items-center gap-3 px-4 py-3 bg-error/10 border border-error/20 rounded-xl text-error text-xs font-black uppercase tracking-widest">
              <Clock size={14} className="flex-shrink-0" />
              This assignment has expired. Fields are read-only.
            </div>
          )}
          <div className="flex flex-col xl:flex-row gap-8">
            {/* Form Column */}
            <div className="w-full xl:w-1/2 order-2 xl:order-1">
              <form onSubmit={handleSubmit} noValidate className="space-y-6">

                <div className="relative" ref={workflowRef}>
                  <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                    Select Capture Workflow *
                  </span>
                  <button
                    type="button"
                    onClick={() => !isReadOnly && setIsWorkflowDropdownOpen(!isWorkflowDropdownOpen)}
                    disabled={isReadOnly}
                    className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-3 bg-base-100 border border-base-300 text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all cursor-pointer group min-w-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-base-200"
                  >
                    <span className="truncate flex-1 text-left min-w-0 block">
                      {selectedWorkflow ? workflows.find(w => w.id === selectedWorkflow)?.name : 'Choose a workflow...'}
                    </span>
                    {!isReadOnly && <ChevronRight size={14} className={`text-base-content/20 group-hover:text-primary transition-transform shrink-0 ${isWorkflowDropdownOpen ? 'rotate-90' : ''}`} />}
                  </button>

                  {isWorkflowDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col max-h-64">
                      {/* Search bar */}
                      <div className="px-3 py-2 border-b border-base-content/10 flex-shrink-0">
                        <div className="relative">
                          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/30" />
                          <input
                            ref={workflowSearchRef}
                            type="text"
                            value={workflowSearch}
                            onChange={e => setWorkflowSearch(e.target.value)}
                            placeholder="Search workflows..."
                            className="w-full pl-7 pr-3 py-1.5 text-xs bg-base-200 border border-base-content/10 rounded-lg outline-none focus:border-primary text-base-content placeholder-base-content/30"
                            autoFocus
                          />
                          {workflowSearch && (
                            <button type="button" onClick={() => setWorkflowSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content">
                              <X size={11} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="overflow-y-auto no-scrollbar flex-1">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedWorkflow('');
                            setIsWorkflowDropdownOpen(false);
                            setWorkflowSearch('');
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 flex items-center gap-2 group ${!selectedWorkflow ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[10px] font-black uppercase tracking-widest ${!selectedWorkflow ? 'text-primary' : 'text-base-content/30 group-hover:text-primary'}`}>Choose a workflow...</span>
                          {!selectedWorkflow && <CheckCircle size={12} className="text-primary ml-auto" />}
                        </button>

                        {workflows
                          .filter((w) =>
                            w.name.toLowerCase().includes(workflowSearch.toLowerCase())
                          )
                          .sort((a, b) =>
                            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                          )
                          .map((w) => (
                            <button
                              key={w.id}
                              type="button"
                              onClick={() => {
                                setSelectedWorkflow(w.id);
                                setIsWorkflowDropdownOpen(false);
                                setWorkflowSearch('');
                                setFieldErrors(prev => ({ ...prev, selectedWorkflow: '' }));
                              }}
                              className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 last:border-none flex items-center gap-2 group ${selectedWorkflow === w.id ? 'bg-primary/5' : ''
                                }`}
                            >
                              <div className="flex flex-col min-w-0 flex-1">
                                <span
                                  className={`text-sm font-bold truncate ${selectedWorkflow === w.id
                                    ? 'text-primary'
                                    : 'text-base-content group-hover:text-primary'
                                    }`}
                                >
                                  {w.name}
                                </span>
                              </div>

                              {selectedWorkflow === w.id && (
                                <CheckCircle size={12} className="text-primary ml-auto" />
                              )}
                            </button>
                          ))}

                        {workflows.filter(w => w.name.toLowerCase().includes(workflowSearch.toLowerCase())).length === 0 && (
                          <div className="px-4 py-6 text-center">
                            <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">No workflows found</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <input type="hidden" value={selectedWorkflow} required name="workflowId" />
                  {fieldErrors.selectedWorkflow && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.selectedWorkflow}</p>
                  )}
                </div>

                {/* Creator Name */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="creator-name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                      Creator Name *
                    </label>
                    <div className={CLASS_INFO_TOOLTIP} data-tip="Person who creates and sends the assignment.">
                      <Info size={10} className="text-base-content/30 cursor-help" />
                    </div>
                  </div>
                  <input
                    id="creator-name"
                    type="text"
                    maxLength={50}
                    value={assigneeName}
                    onChange={e => {
                      setAssigneeName(e.target.value);
                      if (fieldErrors.assigneeName) {
                        setFieldErrors(prev => ({ ...prev, assigneeName: validateTextField(e.target.value, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
                      }
                    }}
                    onBlur={() => handleFieldBlur('assigneeName', assigneeName, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT })}
                    disabled={isReadOnly}
                    placeholder="e.g. Agent 007"
                    className={`${CLASS_FIELD_INPUT} placeholder-base-content/20 ${fieldErrors.assigneeName ? CLASS_FIELD_ERROR_BORDER : CLASS_FIELD_DEFAULT_BORDER} ${CLASS_FIELD_INPUT_DISABLED} `}
                  />
                  {fieldErrors.assigneeName && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.assigneeName}</p>
                  )}
                </div>

                {/* Recipient Name */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="recipient-name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                      Recipient Name *
                    </label>
                    <div className={CLASS_INFO_TOOLTIP} data-tip="Person who gets the link and completes the assignment.">
                      <Info size={10} className="text-base-content/30 cursor-help" />
                    </div>
                  </div>
                  <input
                    id="recipient-name"
                    type="text"
                    value={submitterName}
                    onChange={e => {
                      setSubmitterName(e.target.value);
                      if (fieldErrors.submitterName) {
                        setFieldErrors(prev => ({ ...prev, submitterName: validateTextField(e.target.value, 'Recipient name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
                      }
                    }}
                    onBlur={() => handleFieldBlur('submitterName', submitterName, 'Recipient name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT })}
                    disabled={isReadOnly}
                    placeholder="e.g. John Smith"
                    className={`${CLASS_FIELD_INPUT} placeholder-base-content/20 ${fieldErrors.submitterName ? CLASS_FIELD_ERROR_BORDER : CLASS_FIELD_DEFAULT_BORDER} ${CLASS_FIELD_INPUT_DISABLED}`}
                  />
                  {fieldErrors.submitterName && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.submitterName}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="recipient-phone" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                    Recipient Phone
                  </label>
                  <input
                    id="recipient-phone"
                    type="tel"
                    value={submitterPhone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 000-0000"
                    maxLength={14}
                    disabled={isReadOnly}
                    className={`${CLASS_FIELD_INPUT} placeholder-base-content/20 ${fieldErrors.submitterPhone ? CLASS_FIELD_ERROR_BORDER : CLASS_FIELD_DEFAULT_BORDER} ${CLASS_FIELD_INPUT_DISABLED}`}
                  />
                  {fieldErrors.submitterPhone && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.submitterPhone}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="recipient-email" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                    Recipient Email
                  </label>
                  <input
                    id="recipient-email"
                    type="email"
                    value={submitterEmail}
                    onChange={handleEmailChange}
                    placeholder="name@company.com"
                    disabled={isReadOnly}
                    className={`${CLASS_FIELD_INPUT} placeholder-base-content/20 ${fieldErrors.submitterEmail ? CLASS_FIELD_ERROR_BORDER : CLASS_FIELD_DEFAULT_BORDER} ${CLASS_FIELD_INPUT_DISABLED}`}
                  />
                  {fieldErrors.submitterEmail && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.submitterEmail}</p>
                  )}
                </div>

                {/* Extra fields only for 'created' status (hidden for resending expired) */}
                {(assignment.status === 'created' || assignment.status === 'expired') && (
                  <>
                    {/* Assignee Name moved to top and made required */}

                    <div>
                      <div className="flex items-center gap-1.5 mb-2 min-w-0">
                        <label htmlFor="customer-ref" className="text-[10px] font-black text-base-content/40 tracking-[0.2em] block" title={dynamicCustomerReferenceLabel}>
                          <span className="uppercase">{dynamicCustomerReferenceLabel} *</span>
                          <div className={`${CLASS_INFO_TOOLTIP} ml-2`} data-tip="Your own reference number for this assignment (e.g. policy number, claim number, or any identifier from your system)">
                            <Info size={10} className="text-base-content/30 cursor-help" />
                          </div>
                        </label>
                      </div>
                      <input
                        id="customer-ref"
                        type="text"
                        value={customerReferenceNumber}
                        onChange={e => {
                          setCustomerReferenceNumber(e.target.value);
                          if (fieldErrors.customerReferenceNumber) {
                            setFieldErrors(prev => ({ ...prev, customerReferenceNumber: validateTextField(e.target.value, dynamicCustomerReferenceLabel, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.REFERENCE }) }));
                          }
                        }}
                        onBlur={() => handleFieldBlur('customerReferenceNumber', customerReferenceNumber, dynamicCustomerReferenceLabel, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.REFERENCE })}
                        placeholder="e.g. POL-123456"
                        className={`${CLASS_FIELD_INPUT} placeholder-base-content/20 ${CLASS_FIELD_INPUT_DISABLED}`}
                        disabled={isReadOnly}
                      />
                      {fieldErrors.customerReferenceNumber && (
                        <p className="text-error text-xs mt-1 font-medium">{fieldErrors.customerReferenceNumber}</p>
                      )}
                    </div>

                    {/* System Reference Number */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label htmlFor="system-ref" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                          System Reference Number
                        </label>
                        <div className={CLASS_INFO_TOOLTIP} data-tip="Auto-generated unique identifier for internal tracking purposes">
                          <Info size={10} className="text-base-content/30 cursor-help" />
                        </div>
                      </div>
                      <input
                        id="system-ref"
                        type="text"
                        value={systemReferenceNumber}
                        readOnly
                        placeholder="#CLM-2024-143052"
                        maxLength={32}
                        className="w-full px-4 py-3 bg-base-200 border border-base-300 rounded-xl text-base-content/70 outline-none cursor-not-allowed"
                      />
                    </div>
                  </>
                )}

                {/* Link Expiry */}
                <div>
                  <label htmlFor="expiry-value" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                    Link Expiry
                  </label>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="flex items-center gap-2">
                      <input
                        id="expiry-value"
                        type="number"
                        min="1"
                        max="8760"
                        value={customValue}
                        onWheel={(e) => e.target.blur()}
                        onChange={e => setCustomValue(e.target.value)}
                        placeholder="Enter value"
                        disabled={isReadOnly}
                        className="rounded-md w-24 pl-3 pr-2 py-2.5 bg-base-100 border border-base-300 text-sm font-medium focus:ring-2 focus:ring-primary focus:border-primary outline-none disabled:opacity-50 disabled:bg-base-200"
                      />
                      <select
                        value={customUnit}
                        onChange={e => setCustomUnit(e.target.value)}
                        disabled={isReadOnly}
                        className="rounded-md pl-3 pr-8 py-2.5 bg-base-100 border border-base-300 text-sm font-medium focus:ring-2 focus:ring-primary focus:border-primary outline-none disabled:opacity-50 disabled:bg-base-200"
                      >
                        {TIME_UNITS.map(unit => (
                          <option key={unit.value} value={unit.value}>{unit.label}</option>
                        ))}
                      </select>
                    </div>
                    {customValue && parseInt(customValue, 10) > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/10 rounded-md">
                        <span className="text-[14px] font-black text-base-content/30 uppercase">≈</span>
                        <span className="text-[10px] text-primary font-black uppercase tracking-wider">
                          {formatExpiryLabel(parseInt(customValue, 10), customUnit)}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest mt-2 ml-1">
                    {initialExpiry && customValue === initialExpiry.value && customUnit === initialExpiry.unit
                      ? 'Current expiration time is active.'
                      : 'Time will be recounted from now on save.'}
                  </p>
                </div>

                {/* Single-Session Restriction Toggle */}
                <div className="flex items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
                      Restrict to Single Session
                    </span>
                    <div
                      className="tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:max-w-none before:content-[attr(data-tip)] before:whitespace-nowrap after:border-r-[#fffdf2]"
                      data-tip="When enabled, only one device can actively use this capture link at a time. A conflict modal is shown if another device tries to open it."
                    >
                      <Info size={10} className="text-base-content/30 cursor-help" />
                    </div>
                  </div>
                  <label className={`relative inline-flex items-center ${isReadOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      id="edit-restrict-single-session"
                      type="checkbox"
                      className="sr-only peer"
                      checked={restrictToSingleSession}
                      onChange={(e) => !isReadOnly && setRestrictToSingleSession(e.target.checked)}
                      disabled={isReadOnly}
                    />
                    <div className="relative w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50" />
                  </label>
                </div>

                {error && (
                  <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-xl text-sm font-bold">{error}</div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/assignments/${id}`)}
                    className="flex-1 py-4 border border-base-300 text-base-content/60 rounded-xl font-black uppercase text-xs tracking-[0.2em] hover:bg-base-200 transition-all"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isReadOnly || saving}
                    className="flex-1 py-4 btn btn-primary rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-primary/20 disabled:opacity-50 transition-all border-none flex items-center justify-center gap-2"
                  >
                    <Save size={16} />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>

            {/* Workflow Preview Column */}
            <div className="w-full xl:w-1/2 order-1 xl:order-2">
              {workflowPreview && items.length > 0 && (
                <div className="border border-primary/20 bg-primary/5 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">
                      Template Preview
                    </p>
                    <span className="text-[10px] font-black text-primary/60 uppercase tracking-widest bg-primary/10 px-2 py-1 rounded-lg">
                      {items.length} Screens &bull; {getIndustryLabel(workflowPreview.industry)}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {items.map((item, index) => {
                      const Icon = typeIcons[item.type] || Camera;
                      const typeColors = {
                        photo: 'bg-primary/10 text-primary',
                        video: 'bg-secondary/10 text-secondary',
                        // identifier: 'bg-accent/10 text-accent'
                      };
                      return (
                        <div
                          key={item.id}
                          onClick={() => setPreviewStep(2 + index)}
                          className="flex items-center gap-4 bg-base-100 rounded-xl p-3 border border-base-300 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
                        >
                          <span className="text-xs text-base-content/20 font-black w-4 flex-shrink-0 text-center group-hover:text-primary/40">{(index + 1).toString().padStart(2, '0')}</span>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeColors[item.type] || 'bg-base-200 text-base-content/40'}`}>
                            {item.type === 'identifier' ? <img src={scannerIcon} alt="" className='w-10 h-10 object-contain p-1' /> : item.type === 'file' ? <img src={filesIcon} alt="" className='w-10 h-10 object-contain p-2' /> : <Icon size={18} />}
                          </div>
                          <span className="text-sm text-base-content/80 font-black tracking-tight flex-1 custom-line-break">{item.description}</span>
                          {!item.required && (
                            <span className="text-[9px] font-black uppercase tracking-widest text-base-content/20 bg-base-200 px-2.5 py-1 rounded-full flex-shrink-0">Optional</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {workflowPreview.description && (
                    <p className="text-xs text-primary/60 mt-4 font-bold  tracking-widest px-4 custom-line-break">{workflowPreview.description}</p>
                  )}

                  {customValue && parseInt(customValue, 10) > 0 && (
                    <div className="flex items-center gap-3 mt-6 pt-5 border-t border-primary/10">
                      <Clock size={14} className="text-primary/40 flex-shrink-0" />
                      <p className="text-[10px] font-black uppercase tracking-widest text-primary/40">
                        Link Lifetime: <span className="text-primary">
                          {formatExpiryLabel(parseInt(customValue, 10), customUnit)}
                        </span>
                        <span className="mx-2 opacity-50">&bull;</span>
                        {expiresAtDate.toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right: Phone Preview */}
      {workflowPreview ? (
        <div className="w-full lg:w-[360px] xl:w-[400px] flex-shrink-0 bg-base-200 flex flex-col border-t lg:border-t-0 border-base-300 overflow-y-auto overflow-x-hidden">
          <div className="px-4 py-3 border-b border-base-300 bg-base-100 flex justify-between flex-shrink-0">
            <p className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em]">Live Preview</p>
            <p className="text-[10px] text-base-content/20 uppercase font-black tracking-widest">Interactive view</p>
          </div>

          {/* Screen selector */}
          <div className="px-3 py-2 bg-base-100 border-b border-base-300 flex-shrink-0">
            <div className="flex flex-wrap gap-2">
              {screenLabels.map((label, i) => (
                <button
                  key={i}
                  onClick={() => setPreviewStep(i)}
                  className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${previewStep === i
                    ? 'text-white bg-primary shadow-lg scale-105'
                    : 'bg-base-200 text-base-content/40 hover:bg-base-300'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center p-2">
            <PhonePreview
              workflowName={workflowPreview.name}
              name={welcomeTitle}
              submitterName={submitterName}
              assigneeName={assigneeName}
              customerReferenceNumber={customerReferenceNumber}
              customerReferenceLabel={dynamicCustomerReferenceLabel}
              systemReferenceNumber={systemReferenceNumber}
              industry={workflowPreview.industry}
              description={workflowPreview.description}
              items={items}
              previewStep={previewStep}
              branding={branding}
              expiresAt={expiresAtDate}
            />
          </div>

          {/* Navigation arrows */}
          <div className="px-5 py-1 bg-base-100 border-t border-base-300 flex items-center justify-between flex-shrink-0">
            <button
              onClick={() => setPreviewStep(p => Math.max(0, p - 1))}
              disabled={previewStep === 0}
              className="p-1.5 rounded-lg hover:bg-base-200 disabled:opacity-20 text-base-content transition"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">{previewStep + 1} / {Math.max(1, totalScreens)}</span>
            <button
              onClick={() => setPreviewStep(p => Math.min(totalScreens - 1, p + 1))}
              disabled={previewStep >= totalScreens - 1}
              className="p-1.5 rounded-lg hover:bg-base-200 disabled:opacity-20 text-base-content transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="w-full lg:w-[360px] xl:w-[400px] flex-shrink-0 bg-base-200 flex flex-col items-center justify-center text-center p-8 border-t lg:border-t-0 border-base-300">
          <div className="text-5xl mb-6 opacity-40">📱</div>
          <p className="text-xs font-black text-base-content/40 uppercase tracking-[0.2em]">Select a workflow</p>
          <p className="text-[10px] text-base-content/20 mt-2 uppercase font-black tracking-widest">Preview will sync automatically</p>
        </div>
      )}

      {/* Expire Link Confirmation Modal */}
      <Modal
        isOpen={showExpireModal}
        onClose={handleExpireCancel}
        title="Expire Assignment Link"
      >
        <div className="mb-6">
          <p className="text-base-content/60 text-sm">
            Are you sure you want to expire this assignment link? This action cannot be undone and will prevent any further submissions.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExpireCancel}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={expiring}
          >
            Cancel
          </button>
          <button
            onClick={handleExpireConfirm}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
            disabled={expiring}
          >
            {expiring ? 'Expiring...' : 'Expire Link'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EditAssignment;
